import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import PropTypes from 'prop-types';
import PasskeysHeader from './components/PasskeysHeader';
import PasskeysList from './components/PasskeysList';
import DeleteModal from './components/DeleteModal';
import { PASSKEY_CONSTANTS } from '../../utils/passkeyUtils';
import { 
    usePasskeyFetcher, 
    usePasskeyAddOperation, 
    usePasskeyDeleteOperation,
    useAuthentication
} from '../../hooks/passkeys';

const PasskeysSection = ({ onShowToast, accessToken, userId, ngcmfaExpiry }) => {
    const { instance } = useMsal();
    const maxPasskeys = PASSKEY_CONSTANTS.MAX_PASSKEYS;

    // Custom hooks handle all the complex logic
    const { passkeys, isLoading, error, fetchPasskeys } = usePasskeyFetcher({ 
        accessToken, userId, onShowToast 
    });
    
    const { handleAddPasskey, performAddPasskey } = usePasskeyAddOperation({ 
        accessToken, 
        userId, 
        ngcmfaExpiry, 
        onShowToast, 
        fetchPasskeys,
        currentPasskeys: passkeys
    });
    
    const { initiate: initiateDelete, showConfirmationModal, modalProps } = usePasskeyDeleteOperation({ 
        accessToken, 
        userId, 
        ngcmfaExpiry, 
        onShowToast, 
        fetchPasskeys,
        currentPasskeys: passkeys
    });

    const { getCachedOperation, clearCachedOperation } = useAuthentication({ onShowToast });

    // Handle initial fetch
    useEffect(() => {
        if (accessToken && userId) {
            fetchPasskeys().catch(console.error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, userId]); // Only depend on accessToken and userId, not fetchPasskeys

    useEffect(() => {
        instance.handleRedirectPromise()
            .then((response) => {
                const operation = getCachedOperation();
                
                if (operation && accessToken && userId) {
                    clearCachedOperation();
                    
                    if (operation.action === "add") {
                        performAddPasskey();
                    } else if (operation.action === "delete" && operation.passkey) {
                        showConfirmationModal(operation.passkey);
                    }
                }
            }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, userId]);

    return (
        <>
            <section className="passkeys-section mb-4">
                <PasskeysHeader
                    count={passkeys.length}
                    maxCount={maxPasskeys}
                    onAddClick={handleAddPasskey}
                    isLoading={isLoading}
                />
                <PasskeysList
                    passkeys={passkeys}
                    onDelete={initiateDelete}
                    isLoading={isLoading}
                    error={error}
                />
            </section>
            
            <DeleteModal {...modalProps} />
        </>
    );
};

PasskeysSection.propTypes = {
    onShowToast: PropTypes.func.isRequired,
    accessToken: PropTypes.string,
    userId: PropTypes.string,
    ngcmfaExpiry: PropTypes.number,
};

export default PasskeysSection;
